export const categories = [
  { id: 'general', label: 'General Business' },
  { id: 'bookings', label: 'Bookings & Services' },
  { id: 'health', label: 'Health & Care' },
  { id: 'waivers', label: 'Waivers & Consent' },
  { id: 'events', label: 'Events & Recruitment' },
  { id: 'phone-demo', label: 'Phone Verification' }
];

export const formSchemas = {
  general: [
    {
      id: 'contact-us',
      title: 'Contact Us',
      description: 'Standard business enquiry form with verified contact details.',
      fields: [
        { name: 'fullName', label: 'Full Name', type: 'text', required: true },
        { name: 'mobile', label: 'Mobile', type: 'tel', required: true },
        { name: 'businessName', label: 'Business Name', type: 'text', required: false },
        { name: 'enquiryType', label: 'Enquiry Type', type: 'select', options: ['Sales', 'Support', 'Partnership', 'Other'], required: true },
        { name: 'message', label: 'Message', type: 'textarea', required: true }
      ]
    },
    {
      id: 'quote-request',
      title: 'Quote / Service Request',
      description: 'Detailed service request to capture qualified leads.',
      fields: [
        { name: 'fullName', label: 'Full Name', type: 'text', required: true },
        { name: 'mobile', label: 'Mobile', type: 'tel', required: true },
        { name: 'businessName', label: 'Business Name', type: 'text', required: true },
        { name: 'serviceNeeded', label: 'Service Needed', type: 'text', required: true },
        { name: 'budgetRange', label: 'Budget Range', type: 'select', options: ['Under $1k', '$1k - $5k', '$5k - $10k', '$10k+'], required: true },
        { name: 'preferredTimeframe', label: 'Preferred Timeframe', type: 'select', options: ['ASAP', 'Within 1 month', '1-3 months', 'Just researching'], required: true },
        { name: 'projectDetails', label: 'Project Details', type: 'textarea', required: true }
      ]
    }
  ],
  bookings: [
    {
      id: 'appointment',
      title: 'Appointment Request',
      description: 'Streamlined booking request for service professionals.',
      fields: [
        { name: 'fullName', label: 'Full Name', type: 'text', required: true },
        { name: 'mobile', label: 'Mobile', type: 'tel', required: true },
        { name: 'serviceRequired', label: 'Service Required', type: 'text', required: true },
        { name: 'preferredDate', label: 'Preferred Date', type: 'date', required: true },
        { name: 'timeWindow', label: 'Time Window', type: 'select', options: ['Morning', 'Afternoon', 'Evening'], required: true },
        { name: 'notes', label: 'Notes', type: 'textarea', required: false }
      ]
    }
  ],
  health: [
    {
      id: 'ndis-intake',
      title: 'NDIS Enquiry / Intake',
      description: 'Secure, structured intake for NDIS providers.',
      fields: [
        { name: 'participantName', label: 'Participant Name', type: 'text', required: true },
        { name: 'contactPerson', label: 'Contact Person', type: 'text', required: true },
        { name: 'relationship', label: 'Relationship to Participant', type: 'text', required: true },
        { name: 'mobile', label: 'Mobile', type: 'tel', required: true },
        { name: 'location', label: 'Location / Suburb', type: 'text', required: true },
        { name: 'fundingType', label: 'Funding Type', type: 'select', options: ['Self Managed', 'Plan Managed', 'NDIA Managed', 'Not Sure'], required: true },
        { name: 'summary', label: 'Summary of Needs', type: 'textarea', required: true }
      ]
    }
  ],
  waivers: [
    {
      id: 'dog-walking-waiver',
      title: 'Dog Walking & Pet Sitting Waiver',
      description: 'Digital liability release and service agreement for pet professionals.',
      verificationType: 'email',
      fields: [
        { type: 'sectionHeading', label: 'Owner & Pet Details' },
        { name: 'ownerName', label: 'Owner Full Name', type: 'text', required: true },
        { name: 'petName', label: 'Pet Name(s)', type: 'text', required: true },
        { name: 'emergencyContact', label: 'Emergency Phone', type: 'tel', required: true },
        { type: 'sectionHeading', label: 'Acknowledgement of Risk' },
        { 
          type: 'staticInfo', 
          content: 'I acknowledge that dog walking and pet care involves inherent risks. I authorize the walker to seek emergency veterinary care if required at my expense. I confirm my pet is up to date with vaccinations and has no history of aggression.' 
        },
        { name: 'acceptRisk', label: 'I accept the terms and risks of service', type: 'checkbox', required: true },
        { name: 'signature', label: 'Owner Signature', type: 'signature', required: true }
      ]
    },
    {
      id: 'fitness-waiver',
      title: 'Fitness / Personal Training Waiver',
      description: 'Pre-exercise screening and liability waiver for gyms and trainers.',
      verificationType: 'email',
      fields: [
        { name: 'fullName', label: 'Client Name', type: 'text', required: true },
        { name: 'dob', label: 'Date of Birth', type: 'date', required: true },
        { name: 'healthStatus', label: 'Do you have any heart conditions or injuries?', type: 'radio', options: ['Yes', 'No'], required: true },
        { type: 'sectionHeading', label: 'Declaration' },
        { 
          type: 'staticInfo', 
          content: 'I understand that physical exercise can be strenuous and has risk of injury. I confirm I am physically fit to participate and waive any claim against the trainer/facility for injuries sustained during training.' 
        },
        { name: 'acceptTerms', label: 'I have read and understood the exercise waiver', type: 'checkbox', required: true },
        { name: 'signature', label: 'Participant Signature', type: 'signature', required: true }
      ]
    },
    {
      id: 'beauty-consent',
      title: 'Beauty Treatment Consent',
      description: 'Detailed consent record for skin treatments, cosmetic injectables, or lash services.',
      verificationType: 'email',
      fields: [
        { name: 'fullName', label: 'Client Name', type: 'text', required: true },
        { name: 'treatmentType', label: 'Treatment Applied For', type: 'select', options: ['Chemical Peel', 'Microneedling', 'Lash Extensions', 'Brow Lamination'], required: true },
        { type: 'sectionHeading', label: 'Health Screening' },
        { name: 'allergies', label: 'List any known skin allergies', type: 'textarea', required: true },
        { name: 'aftercare', label: 'I agree to follow the provided aftercare instructions', type: 'checkbox', required: true },
        { name: 'signature', label: 'Client Signature', type: 'signature', required: true }
      ]
    },
    {
      id: 'parental-consent',
      title: 'Kids Activity Parent Consent',
      description: 'Verified guardian permission for youth sports, excursions, or workshops.',
      verificationType: 'email',
      fields: [
        { name: 'childName', label: 'Child Name', type: 'text', required: true },
        { name: 'parentName', label: 'Parent/Guardian Name', type: 'text', required: true },
        { name: 'medications', label: 'Medical conditions or medications', type: 'textarea', required: false },
        { type: 'sectionHeading', label: 'Permission' },
        { 
          type: 'staticInfo', 
          content: 'I give permission for my child to participate in the activity. I authorize the organizers to provide first aid or seek medical assistance if necessary. I agree to the use of photos for promotional purposes (optional below).' 
        },
        { name: 'photoPerm', label: 'I consent to promotional photography', type: 'checkbox', required: false },
        { name: 'consent', label: 'I provide full parental/guardian consent', type: 'checkbox', required: true },
        { name: 'signature', label: 'Guardian Signature', type: 'signature', required: true }
      ]
    },
    {
      id: 'equipment-hire',
      title: 'Equipment Hire Agreement',
      description: 'Verified rental agreement for tools, machinery, or event equipment.',
      verificationType: 'email',
      fields: [
        { name: 'fullName', label: 'Hirer Name', type: 'text', required: true },
        { name: 'idNumber', label: 'Driver License / ID Number', type: 'text', required: true },
        { name: 'hirePeriod', label: 'Hire Duration', type: 'select', options: ['Daily', 'Weekly', 'Weekend'], required: true },
        { type: 'sectionHeading', label: 'Hire Terms' },
        { 
          type: 'staticInfo', 
          content: 'Equipment must be returned in the same condition as hired. The hirer is responsible for any damage or loss. Hire fees are non-refundable once the equipment leaves the premises.' 
        },
        { name: 'agreeTerms', label: 'I agree to the hire terms and conditions', type: 'checkbox', required: true },
        { name: 'signature', label: 'Hirer Signature', type: 'signature', required: true }
      ]
    }
  ],
  events: [
    {
      id: 'event-registration',
      title: 'Event Registration',
      description: 'Clean registration flow for corporate or community events.',
      fields: [
        { name: 'fullName', label: 'Full Name', type: 'text', required: true },
        { name: 'mobile', label: 'Mobile', type: 'tel', required: true },
        { name: 'eventName', label: 'Event Name', type: 'text', required: true },
        { name: 'attendees', label: 'Number of Attendees', type: 'select', options: ['1', '2', '3', '4', '5+'], required: true }
      ]
    }
  ],
  'phone-demo': [
    {
      id: 'mobile-verified-enquiry',
      title: 'Mobile Verified Enquiry',
      description: 'High-security intake requiring SMS verification of an Australian mobile number.',
      verificationType: 'phone',
      fields: [
        { name: 'fullName', label: 'Full Name', type: 'text', required: true },
        { name: 'email', label: 'Email Address', type: 'email', required: true },
        { name: 'businessName', label: 'Business Name', type: 'text', required: false },
        { name: 'enquiryType', label: 'Enquiry Type', type: 'select', options: ['General', 'Sales', 'Partnership', 'Urgent'], required: true },
        { name: 'bestTimeToCall', label: 'Best Time to Call', type: 'select', options: ['Morning', 'Afternoon', 'Evening'], required: true },
        { name: 'suburb', label: 'Suburb', type: 'text', required: true },
        { name: 'message', label: 'Short Message', type: 'textarea', required: true }
      ]
    }
  ]
};